/**
 * Plan Entity Schemas
 *
 * Mirrors the Prisma `Plan` + `PlanFeature` models
 * (apps/backend/prisma/schema/base.prisma). A Plan belongs to a Product and
 * is the baseline grant set for a Subscription. A PlanFeature links the plan
 * to a `Component` (the feature catalog, reused per KTD-4) with an enabled
 * flag and an optional per-plan config override.
 *
 * This is the fresh, clean `Plan` (KTD-2a): the legacy `SubscriptionPlan`
 * and its cost fields (monthlyCost/annualCost/startupCost) are dropped — the
 * platform never charges, so list-price has no home here.
 *
 * `publicId` is exposed as `id`; raw BigInt internal ids are never surfaced.
 */

import { z } from 'zod'

/**
 * A plan's grant of a single feature. `featureSlug` is the `Feature.slug`
 * the grant refers to (KTD-4); `config` is the optional per-plan override of
 * the feature's default config.
 */
export const PlanFeatureSchema = z.object({
  id: z.string().describe('Plan-feature public ID (cuid)'),
  featureId: z.string().describe('Granted Feature public ID'),
  featureSlug: z.string().nullable().describe('Granted Feature slug (feature identifier)'),
  isEnabled: z.boolean().describe('Whether the feature is granted by this plan'),
  config: z.record(z.string(), z.unknown()).nullable().describe('Optional per-plan config override for the feature'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type PlanFeature = z.infer<typeof PlanFeatureSchema>

export const PlanSchema = z.object({
  id: z.string().describe('Plan public ID (cuid)'),
  productId: z.string().describe('Owning Product public ID'),
  slug: z.string().describe('Plan identifier (unique within its product)'),
  name: z.string().describe('Plan display name'),
  description: z.string().nullable().describe('Plan description'),
  isActive: z.boolean().describe('Whether the plan is offered'),
  features: z.array(PlanFeatureSchema).describe('Baseline feature grants for this plan'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type Plan = z.infer<typeof PlanSchema>

/**
 * Minimal plan reference for nesting inside subscriptions / list views.
 */
export const PlanSummarySchema = PlanSchema.pick({
  id: true,
  productId: true,
  slug: true,
  name: true
})

export type PlanSummary = z.infer<typeof PlanSummarySchema>
